import { useEffect, useState } from "react";
import { syncApiUrl, getAuthToken } from "@cliqsuite/db";
import type { Payment } from "../lib/adminTypes";
import { chipStyle, mmk, statusMeta } from "../lib/format";

interface DetailRow {
  label: string;
  value: string;
  mono?: boolean;
  canCopy?: boolean;
}

export function PaymentDetailDrawer({
  payment,
  onClose,
  onApprove,
  onReject,
  onResubmit,
}: {
  payment: Payment;
  onClose: () => void;
  onApprove: () => void;
  onReject: () => void;
  onResubmit: () => void;
}) {
  const sm = statusMeta(payment.status);

  // payment.proof is now a private-bucket object key (payment-proofs is no
  // longer public-read — see services/sync-api/src/lib/storage.ts), not a
  // directly-loadable URL — fetch a short-lived signed URL to actually
  // display it.
  const [proofViewUrl, setProofViewUrl] = useState<string | null>(null);
  useEffect(() => {
    setProofViewUrl(null);
    if (!payment.proof) return;
    let cancelled = false;
    void (async () => {
      const res = await fetch(syncApiUrl(`/uploads/payment-proof?key=${encodeURIComponent(payment.proof!)}`), {
        headers: getAuthToken() ? { authorization: `Bearer ${getAuthToken()}` } : {},
      });
      if (!res.ok || cancelled) return;
      const { url } = (await res.json()) as { url: string };
      if (!cancelled) setProofViewUrl(url);
    })();
    return () => {
      cancelled = true;
    };
  }, [payment.proof]);

  const rows: DetailRow[] = [
    { label: "Business", value: payment.business },
    { label: "Type", value: payment.businessType },
    { label: "Billing email", value: payment.billingEmail || "—" },
    { label: "Method", value: payment.methodLabel },
    { label: "Submitted", value: payment.dateLabel },
  ];
  if (payment.txid) rows.push({ label: "TX ref", value: payment.txid, mono: true, canCopy: true });
  if (payment.notes) rows.push({ label: "Notes from the business", value: payment.notes });

  const copy = (text: string) => {
    try {
      void navigator.clipboard?.writeText(text);
    } catch {
      // ignore
    }
  };

  const reviewed = payment.status !== "pending";
  const submitted = payment.dateLabel.replace(/, \d{4}$/, "");

  return (
    <div className="o-scrim o-dialog--side" style={{ zIndex: 120 }} onClick={onClose}>
      <section className="o-dialog" role="dialog" aria-modal="true" aria-label={payment.ref} onClick={(e) => e.stopPropagation()} style={{ ["--w" as string]: "560px" }}>
        <header className="o-dialog__head">
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 className="num">{payment.ref}</h2>
            <div className="o-row__sub" style={{ fontSize: 13 }}>
              {[payment.business, `submitted ${submitted}`, payment.resubmitted ? "sent again" : null].filter(Boolean).join(", ")}
            </div>
          </div>
          <span className="o-pill" style={chipStyle(sm)}>{sm.label}</span>
          <button type="button" aria-label="Close" className="o-close" onClick={onClose}>
            <span className="ms" aria-hidden="true">close</span>
          </button>
        </header>

        <div className="o-dialog__body">
          <div className="o-proof">
            <div className="o-field__label" style={{ marginBottom: 10 }}>Payment proof</div>
            {proofViewUrl ? (
              <img src={proofViewUrl} alt={`Payment proof for ${payment.ref}`} />
            ) : (
              <div className="o-proof__none">
                <span className="ms" aria-hidden="true">image_not_supported</span>
                {payment.proof ? "Loading screenshot…" : "No screenshot attached"}
              </div>
            )}
          </div>

          <div className="o-kvlist">
            <div>
              <span>Amount</span>
              <strong className="num" style={{ fontSize: 18 }}>{mmk(payment.amount)}</strong>
            </div>
            <div>
              <span>Plan</span>
              <strong>
                {payment.planName}, {payment.cycleLabel.toLowerCase()}
              </strong>
            </div>
            {rows.map((row) => (
              <div key={row.label}>
                <span>{row.label}</span>
                <strong className={row.mono ? "num" : undefined} style={{ wordBreak: "break-word" }}>{row.value}</strong>
                {row.canCopy && (
                  <button type="button" className="o-icon-btn" aria-label={`Copy ${row.label}`} onClick={() => copy(row.value)}>
                    <span className="ms" aria-hidden="true">content_copy</span>
                  </button>
                )}
              </div>
            ))}
          </div>

          {reviewed && (
            <div className={`o-note ${payment.status === "approved" ? "" : "o-note--warn"}`} style={payment.status === "approved" ? { background: "var(--ok-soft)", color: "var(--ok)" } : undefined}>
              <span className="ms" aria-hidden="true">{payment.status === "approved" ? "check_circle" : "cancel"}</span>
              <span>
                {payment.status === "approved"
                  ? `Approved by ${payment.reviewedBy || "an admin"}${payment.periodEnd ? `, active until ${payment.periodEnd}` : ""}.`
                  : `Rejected by ${payment.reviewedBy || "an admin"}: ${payment.rejectReason || "no reason recorded"}.`}
              </span>
            </div>
          )}
        </div>

        {payment.status === "pending" && (
          <footer className="o-dialog__foot">
            <button type="button" className="o-btn o-btn--danger" onClick={onReject}>
              <span className="ms" aria-hidden="true">block</span>
              Reject
            </button>
            <span style={{ flex: 1 }} />
            <button type="button" className="o-btn o-btn--grey" onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="o-btn o-btn--ink" onClick={onApprove}>
              <span className="ms" aria-hidden="true">check</span>
              Approve & activate
            </button>
          </footer>
        )}
        {payment.status === "rejected" && (
          <footer className="o-dialog__foot">
            <span className="o-row__sub" style={{ flex: 1, whiteSpace: "normal" }}>Sends it back to pending so the business can send new proof.</span>
            <button type="button" className="o-btn o-btn--grey" onClick={onResubmit}>
              <span className="ms" aria-hidden="true">replay</span>
              Reopen
            </button>
          </footer>
        )}
      </section>
    </div>
  );
}
